import { useCallback, useEffect, useState } from "react";
import { fetchTvtSeries } from "../services/api.js";

/**
 * Windowed TVT history for the Dashboard's TVT Backing chart. Backend
 * (tvt.controller.js → series) does the filtering + downsampling; the
 * frontend just renders so multiple surfaces converge on the same
 * shape regardless of who queries.
 */
export function useTvtSeries({ windowId = "30d", samples = 61, pollMs = 60_000 } = {}) {
    const [data, setData] = useState(null);
    const [isLoading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // `opts.background === true` = silent poll refresh: keep the current
    // chart on screen while re-fetching so it never flips back to the
    // loading state on the 60s poll. Only foreground calls toggle loading
    // / null the data on error.
    const refresh = useCallback(async (opts = {}) => {
        const background = opts.background === true;
        if (!background) {
            setLoading(true);
            setError(null);
        }
        try {
            const resp = await fetchTvtSeries({ windowId, samples });
            setData({
                samples: (resp.samples || []).map((s) => ({
                    timestamp: Number(s.timestamp),
                    tvt: BigInt(s.tvt || "0"),
                    circulatingSupply: BigInt(s.circulatingSupply || "0"),
                    totalAllocated: BigInt(s.totalAllocated || "0")
                })),
                peak: resp.peak
                    ? { timestamp: Number(resp.peak.timestamp), tvt: BigInt(resp.peak.tvt || "0") }
                    : null,
                windowId: resp.windowId,
                fromTime: resp.fromTime,
                toTime: resp.toTime
            });
            setError(null);
        } catch (err) {
            setError(err?.message || "Failed to fetch TVT series");
            if (!background) setData(null);
        } finally {
            if (!background) setLoading(false);
        }
    }, [windowId, samples]);

    useEffect(() => {
        refresh();
        if (!pollMs) return undefined;
        const handle = setInterval(() => refresh({ background: true }), pollMs);
        return () => clearInterval(handle);
    }, [refresh, pollMs]);

    return { data, isLoading, error, refresh };
}
